import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { Schemas } from '@crm/contracts';
import { unwrap, type Api } from '../api/client.ts';
import { qk } from '../api/queryKeys.ts';
import { saveBlob } from '../imports/data.ts';
import { todayIso } from '../lib/format.ts';
import { Dec, formatDecimal, formatMoney, formatQuantity } from '../lib/money.ts';
import i18n from '../i18n/index.ts';
import { useTenant } from '../tenant/TenantProvider.tsx';

export type Metric = Schemas['AnalyticsMetric'];
export type Dimension = Schemas['AnalyticsDimension'];
export type Grain = Schemas['AnalyticsGrain'];
export type Compare = Schemas['AnalyticsCompare'];
export type CurrencyMode = Schemas['AnalyticsCurrencyMode'];
export type Unit = Schemas['AnalyticsUnit'];
export type Catalog = Schemas['AnalyticsCatalog'];
export type CatalogMetric = Catalog['metrics'][number];
export type AnalyticsQuery = Schemas['AnalyticsQuery'];
export type AnalyticsFilters = Schemas['AnalyticsFilters'];
export type AnalyticsResult = Schemas['AnalyticsResult'];
export type AnalyticsRow = Schemas['AnalyticsRow'];
export type AnalyticsValue = Schemas['AnalyticsValue'];
export type AnalyticsMember = Schemas['AnalyticsMember'];
export type EntitiesComparison = Schemas['EntitiesComparison'];
export type RefreshStatus = Schemas['AnalyticsRefreshStatus'];
export type RefreshRun = Schemas['AnalyticsRefreshRun'];
export type ExchangeRate = Schemas['ExchangeRate'];
export type MissingRate = AnalyticsResult['missing_rates'][number];

/** Ordre du catalogue (contrat analyse § 3). */
export const METRICS: Metric[] = [
  'REVENUE', 'QUANTITY', 'ORDERS', 'AVG_ORDER', 'ACTIVE_CUSTOMERS', 'NEW_CUSTOMERS', 'LOST_CUSTOMERS', 'QUOTE_CONVERSION', 'APPROVAL_DELAY',
  'COLLECTIONS', 'OVERDUE_SHARE', 'DSO', 'VISITS', 'VISITS_PER_REP_DAY', 'COVERAGE', 'COMPLAINTS', 'COMPLAINTS_PER_100_ORDERS', 'RESOLUTION_TIME', 'TARGET_ACHIEVEMENT',
];
export const DIMENSIONS: Dimension[] = ['ENTITY', 'SECTOR', 'REP', 'ACCOUNT', 'SEGMENT', 'ACCOUNT_TYPE', 'PRODUCT_FAMILY', 'PRODUCT', 'COMPLAINT_CATEGORY'];
export const GRAINS: Grain[] = ['MONTH', 'WEEK', 'DAY'];
export const COMPARES: Compare[] = ['NONE', 'PREVIOUS_PERIOD', 'PREVIOUS_YEAR'];

/** Unités du catalogue (repli tant que `GET /analytics/metrics` n'a pas répondu ; le catalogue du serveur fait foi). */
export const DEFAULT_UNITS: Record<Metric, Unit> = {
  REVENUE: 'MONEY', QUANTITY: 'QUANTITY', ORDERS: 'COUNT', AVG_ORDER: 'MONEY', ACTIVE_CUSTOMERS: 'COUNT', NEW_CUSTOMERS: 'COUNT', LOST_CUSTOMERS: 'COUNT',
  QUOTE_CONVERSION: 'PERCENT', APPROVAL_DELAY: 'HOURS', COLLECTIONS: 'MONEY', OVERDUE_SHARE: 'PERCENT', DSO: 'DAYS', VISITS: 'COUNT', VISITS_PER_REP_DAY: 'RATIO',
  COVERAGE: 'PERCENT', COMPLAINTS: 'COUNT', COMPLAINTS_PER_100_ORDERS: 'RATIO', RESOLUTION_TIME: 'HOURS', TARGET_ACHIEVEMENT: 'PERCENT',
};

export const MAX_METRICS = 6;
export const MAX_DIMENSIONS = 2;
export const MAX_LIMIT = 500;
export const DEFAULT_LIMIT = 100;

/** Chemins des écrans du module. */
export const ANA_PATH = {
  root: '/analyses',
  cockpit: '/analyses/cockpit',
  explorer: '/analyses/explorateur',
  entities: '/analyses/filiales',
  rates: '/analyses/taux-de-change',
  refresh: '/analyses/actualisations',
} as const;

/** Personne sans restriction de périmètre (comparaison des filiales, actualisations, journal). */
export function isUnrestricted(context: { scopes: unknown[] } | undefined): boolean {
  return (context?.scopes.length ?? 1) === 0;
}

/** Comparaison des filiales : `analytics.group` ET aucune restriction de périmètre (contrat analyse § 2.3). */
export function canCompareEntities(context: { scopes: unknown[] } | undefined, can: (p: string) => boolean): boolean {
  return can('analytics.read') && can('analytics.group') && isUnrestricted(context);
}

/** Lancer une actualisation : `analytics.config` ET aucune restriction de périmètre (OpenAPI `startAnalyticsRefresh`). */
export function canStartRefresh(context: { scopes: unknown[] } | undefined, can: (p: string) => boolean): boolean {
  return can('analytics.config') && isUnrestricted(context);
}

// ---------------------------------------------------------------- dates (chaînes AAAA-MM-JJ, calcul en UTC)

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isDate(v: string | null | undefined): v is string {
  if (!v || !DATE.test(v)) return false;
  const d = new Date(`${v}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v;
}

const pad = (n: number) => String(n).padStart(2, '0');
const ymd = (y: number, m: number, d: number) => {
  const dt = new Date(Date.UTC(y, m - 1, d));
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
};
const parts = (date: string) => date.split('-').map(Number) as [number, number, number];

/** Nombre de jours d'une période, bornes comprises. */
export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000) + 1;
}

/**
 * Dernier jour permis pour une période de `months` mois commençant le `from` : `from` + `months` mois − 1 jour, le jour
 * ramené à la fin du mois s'il n'existe pas (31 janvier + 1 mois = 28 février), comme le serveur (`interval`).
 */
export function maxPeriodEnd(from: string, months: number): string {
  const [y, m, d] = parts(from);
  const last = new Date(Date.UTC(y, m - 1 + months + 1, 0)).getUTCDate();
  const end = new Date(Date.UTC(y, m - 1 + months, Math.min(d, last)));
  end.setUTCDate(end.getUTCDate() - 1);
  return end.toISOString().slice(0, 10);
}

/** Période au-delà de la limite du serveur (`to` après `from` + `months` mois − 1 jour) ? */
export function exceedsMonths(from: string, to: string, months: number): boolean {
  return to > maxPeriodEnd(from, months);
}

export const PRESETS = ['ce-mois', 'mois-dernier', 'trimestre', '12-mois', 'annee', 'perso'] as const;
export type Preset = (typeof PRESETS)[number];

/**
 * Bornes d'un raccourci de période (relatif au jour : un lien partagé « 12 derniers mois » reste relatif) :
 * ce mois (du 1er à aujourd'hui), mois dernier (complet), trimestre civil en cours, 12 derniers mois (mois courant
 * compris), année civile en cours ; `perso` : les dates données.
 */
export function presetRange(preset: Preset, today: string, custom?: { from: string; to: string }): { from: string; to: string } {
  const [y, m] = parts(today);
  switch (preset) {
    case 'ce-mois':
      return { from: ymd(y, m, 1), to: today };
    case 'mois-dernier':
      return { from: ymd(y, m - 1, 1), to: ymd(y, m, 0) };
    case 'trimestre':
      return { from: ymd(y, Math.floor((m - 1) / 3) * 3 + 1, 1), to: today };
    case '12-mois':
      return { from: ymd(y, m - 11, 1), to: today };
    case 'annee':
      return { from: ymd(y, 1, 1), to: today };
    case 'perso':
      return custom && isDate(custom.from) && isDate(custom.to) ? custom : { from: ymd(y, m, 1), to: today };
  }
}

// ---------------------------------------------------------------- état de l'explorateur ↔ URL

export interface FilterDef {
  key: string;
  /** Paramètre d'URL (français). */
  param: string;
  field: Exclude<keyof AnalyticsFilters, 'unit_code' | 'currency'>;
  dimension: Dimension;
}

export const FILTER_DEFS: FilterDef[] = [
  { key: 'entity', param: 'filiale', field: 'entity_ids', dimension: 'ENTITY' },
  { key: 'sector', param: 'secteur', field: 'sector_ids', dimension: 'SECTOR' },
  { key: 'rep', param: 'commercial', field: 'rep_membership_ids', dimension: 'REP' },
  { key: 'account', param: 'client', field: 'account_ids', dimension: 'ACCOUNT' },
  { key: 'segment', param: 'segment', field: 'segment_ids', dimension: 'SEGMENT' },
  { key: 'accountType', param: 'type', field: 'account_type_ids', dimension: 'ACCOUNT_TYPE' },
  { key: 'family', param: 'famille', field: 'product_family_ids', dimension: 'PRODUCT_FAMILY' },
  { key: 'product', param: 'article', field: 'product_ids', dimension: 'PRODUCT' },
  { key: 'category', param: 'categorie', field: 'complaint_category_ids', dimension: 'COMPLAINT_CATEGORY' },
];
export const filterOfDimension = (d: Dimension) => FILTER_DEFS.find((f) => f.dimension === d)!;
/** Au plus 200 identifiants par filtre (contrat `AnalyticsFilters`). */
export const MAX_FILTER_IDS = 200;

export type FilterValues = Record<FilterDef['field'], string[]>;

export interface ExplorerState {
  metrics: Metric[];
  dimensions: Dimension[];
  grain: Grain | null;
  preset: Preset;
  /** Dates choisies (raccourci `perso` seulement). */
  from: string;
  to: string;
  compare: Compare;
  currencyMode: CurrencyMode;
  filters: FilterValues;
  /** Unité retenue pour le volume (`QUANTITY`). */
  unit: string;
  /** NATIVE : ne garder qu'une devise. */
  currency: string;
  sort: { metric: Metric; direction: 'asc' | 'desc' } | null;
  limit: number;
}

const GRAIN_PARAM: Record<Grain | 'NONE', string> = { MONTH: 'mois', WEEK: 'semaine', DAY: 'jour', NONE: 'aucun' };
const COMPARE_PARAM: Record<Compare, string> = { NONE: '', PREVIOUS_PERIOD: 'periode-precedente', PREVIOUS_YEAR: 'annee-precedente' };
const MODE_PARAM: Record<CurrencyMode, string> = { NATIVE: '', REPORTING: 'consolidee' };

const listParam = <T extends string>(v: string | null, allowed: readonly T[], max: number): T[] =>
  [...new Set((v ?? '').split(',').map((x) => x.trim()).filter((x): x is T => (allowed as readonly string[]).includes(x)))].slice(0, max);
const idsParam = (v: string | null): string[] => [...new Set((v ?? '').split(',').map((x) => x.trim().toLowerCase()).filter((x) => UUID.test(x)))].slice(0, MAX_FILTER_IDS);
const keyOf = <K extends string>(map: Record<K, string>, v: string | null, fallback: K): K => (Object.keys(map) as K[]).find((k) => map[k] === (v ?? '')) ?? fallback;

export const DEFAULT_STATE: ExplorerState = {
  metrics: ['REVENUE'],
  dimensions: [],
  grain: 'MONTH',
  preset: '12-mois',
  from: '',
  to: '',
  compare: 'NONE',
  currencyMode: 'NATIVE',
  filters: Object.fromEntries(FILTER_DEFS.map((f) => [f.field, []])) as unknown as FilterValues,
  unit: '',
  currency: '',
  sort: null,
  limit: DEFAULT_LIMIT,
};

/** URL → état (lecture tolérante : une valeur inconnue est ignorée, jamais une erreur). */
export function explorerFromParams(p: URLSearchParams): ExplorerState {
  const metrics = listParam(p.get('ind'), METRICS, MAX_METRICS);
  const grainKey = keyOf<Grain | 'NONE'>(GRAIN_PARAM, p.get('grain') ?? 'mois', 'MONTH');
  const from = p.get('du');
  const to = p.get('au');
  const custom = isDate(from) && isDate(to);
  const presetRaw = p.get('periode') as Preset | null;
  const preset: Preset = custom ? 'perso' : presetRaw && PRESETS.includes(presetRaw) && presetRaw !== 'perso' ? presetRaw : DEFAULT_STATE.preset;
  const sortMetric = listParam(p.get('tri'), METRICS, 1)[0];
  const limit = Number.parseInt(p.get('limite') ?? '', 10);
  const unit = (p.get('unite') ?? '').trim().slice(0, 20);
  const currency = (p.get('devise') ?? '').trim().toUpperCase();
  return {
    metrics: metrics.length ? metrics : DEFAULT_STATE.metrics,
    dimensions: listParam(p.get('dim'), DIMENSIONS, MAX_DIMENSIONS),
    grain: grainKey === 'NONE' ? null : grainKey,
    preset,
    from: custom ? from! : '',
    to: custom ? to! : '',
    compare: keyOf(COMPARE_PARAM, p.get('comparer'), 'NONE'),
    currencyMode: keyOf(MODE_PARAM, p.get('mode'), 'NATIVE'),
    filters: Object.fromEntries(FILTER_DEFS.map((f) => [f.field, idsParam(p.get(f.param))])) as unknown as FilterValues,
    unit,
    currency: /^[A-Z]{3}$/.test(currency) ? currency : '',
    sort: sortMetric && (metrics.length ? metrics : DEFAULT_STATE.metrics).includes(sortMetric) ? { metric: sortMetric, direction: p.get('ordre') === 'asc' ? 'asc' : 'desc' } : null,
    limit: Number.isFinite(limit) && limit >= 1 && limit <= MAX_LIMIT ? limit : DEFAULT_LIMIT,
  };
}

/** État → URL courte (les valeurs par défaut n'y figurent pas). */
export function explorerToParams(s: ExplorerState): URLSearchParams {
  const p = new URLSearchParams();
  const metrics = s.metrics.join(',');
  if (metrics !== DEFAULT_STATE.metrics.join(',')) p.set('ind', metrics);
  if (s.dimensions.length) p.set('dim', s.dimensions.join(','));
  if (s.grain !== 'MONTH') p.set('grain', GRAIN_PARAM[s.grain ?? 'NONE']);
  if (s.preset === 'perso' && isDate(s.from) && isDate(s.to)) {
    p.set('du', s.from);
    p.set('au', s.to);
  } else if (s.preset !== DEFAULT_STATE.preset && s.preset !== 'perso') p.set('periode', s.preset);
  if (s.compare !== 'NONE') p.set('comparer', COMPARE_PARAM[s.compare]);
  if (s.currencyMode !== 'NATIVE') p.set('mode', MODE_PARAM[s.currencyMode]);
  for (const f of FILTER_DEFS) if (s.filters[f.field].length) p.set(f.param, s.filters[f.field].join(','));
  if (s.unit.trim()) p.set('unite', s.unit.trim());
  if (s.currency) p.set('devise', s.currency);
  if (s.sort) {
    p.set('tri', s.sort.metric);
    if (s.sort.direction === 'asc') p.set('ordre', 'asc');
  }
  if (s.limit !== DEFAULT_LIMIT) p.set('limite', String(s.limit));
  return p;
}

/** Corps de `POST /analytics/query` (et de l'export) pour un état et un jour donnés. */
export function toQuery(s: ExplorerState, today = todayIso()): AnalyticsQuery {
  const { from, to } = presetRange(s.preset, today, { from: s.from, to: s.to });
  const filters: AnalyticsFilters = {};
  for (const f of FILTER_DEFS) if (s.filters[f.field].length) filters[f.field] = s.filters[f.field];
  if (s.metrics.includes('QUANTITY') && s.unit.trim()) filters.unit_code = s.unit.trim();
  if (s.currencyMode === 'NATIVE' && s.currency) filters.currency = s.currency;
  return {
    metrics: s.metrics,
    dimensions: s.dimensions,
    grain: s.grain,
    from,
    to,
    compare: s.compare,
    filters,
    currency_mode: s.currencyMode,
    sort: s.sort,
    limit: s.limit,
  };
}

// ---------------------------------------------------------------- contrôles avant envoi

export type Problem =
  | { kind: 'noMetric' }
  | { kind: 'tooManyMetrics' }
  | { kind: 'tooManyDimensions' }
  | { kind: 'incompatible'; metric: Metric; dimension: Dimension }
  | { kind: 'filterIncompatible'; metric: Metric; dimension: Dimension }
  | { kind: 'dayTooLong'; days: number; max: number }
  | { kind: 'periodTooLong'; until: string; max: number }
  | { kind: 'periodOrder' }
  | { kind: 'quantityFilter' }
  | { kind: 'quantityUnit' }
  | { kind: 'noReportingCurrency' }
  | { kind: 'grainNotAllowed'; metric: Metric; grain: Grain }
  | { kind: 'wholeMonths'; metric: Metric };

export function catalogMetric(catalog: Catalog | undefined, metric: Metric): CatalogMetric | undefined {
  return catalog?.metrics.find((m) => m.code === metric);
}

/** Dimension permise pour l'indicateur (catalogue du serveur ; sans catalogue, rien n'est refusé ici : le serveur juge). */
export function dimensionAllowed(catalog: Catalog | undefined, metric: Metric, dimension: Dimension): boolean {
  const m = catalogMetric(catalog, metric);
  return !m || m.allowed_dimensions.includes(dimension);
}

/** Indicateurs choisis qui refusent la dimension (motif affiché à côté du choix désactivé). */
export function blockingMetrics(catalog: Catalog | undefined, metrics: Metric[], dimension: Dimension): Metric[] {
  return metrics.filter((m) => !dimensionAllowed(catalog, m, dimension));
}

/** Dimensions choisies que l'indicateur refuse. */
export function blockingDimensions(catalog: Catalog | undefined, metric: Metric, dimensions: Dimension[]): Dimension[] {
  return dimensions.filter((d) => !dimensionAllowed(catalog, metric, d));
}

/** Grain permis pour l'indicateur (`allowed_grains` ; [] = sans découpage par période seulement ; sans grain toujours permis). */
export function grainAllowed(catalog: Catalog | undefined, metric: Metric, grain: Grain | null): boolean {
  if (!grain) return true;
  const m = catalogMetric(catalog, metric);
  return !m || m.allowed_grains.includes(grain);
}

/** Indicateurs choisis qui refusent le grain (motif affiché à côté du choix désactivé). */
export function blockingGrain(catalog: Catalog | undefined, metrics: Metric[], grain: Grain | null): Metric[] {
  return metrics.filter((m) => !grainAllowed(catalog, m, grain));
}

/** L'indicateur exige une période en mois entiers (`whole_months`, ex. atteinte des objectifs). */
export function needsWholeMonths(catalog: Catalog | undefined, metric: Metric): boolean {
  return catalogMetric(catalog, metric)?.whole_months ?? false;
}

/** Période du 1er d'un mois au dernier jour d'un mois ? */
export function isWholeMonths(from: string, to: string): boolean {
  const [ty, tm, td] = parts(to);
  return from.endsWith('-01') && td === new Date(Date.UTC(ty, tm, 0)).getUTCDate();
}

/** Le volume exige un filtre article ou famille et une unité (contrat analyse § 3). */
export function needsProductFilter(catalog: Catalog | undefined, metric: Metric): boolean {
  return catalogMetric(catalog, metric)?.needs_product_filter ?? metric === 'QUANTITY';
}

/**
 * Ce qui empêche d'envoyer la requête, contrôlé avant le serveur (qui reste juge et répond 422 avec sa raison) :
 * combinaisons interdites par le catalogue, grain jour au-delà de 93 jours, période au-delà de 24 mois, volume sans
 * filtre article ou famille et sans unité, consolidation sans devise de consolidation.
 */
export function queryProblems(s: ExplorerState, catalog: Catalog | undefined, today = todayIso()): Problem[] {
  const out: Problem[] = [];
  const q = toQuery(s, today);
  if (!s.metrics.length) out.push({ kind: 'noMetric' });
  if (s.metrics.length > MAX_METRICS) out.push({ kind: 'tooManyMetrics' });
  if (s.dimensions.length > MAX_DIMENSIONS) out.push({ kind: 'tooManyDimensions' });
  for (const m of s.metrics) for (const d of s.dimensions) if (!dimensionAllowed(catalog, m, d)) out.push({ kind: 'incompatible', metric: m, dimension: d });
  // Un filtre porte sur une dimension : il obéit aux mêmes règles du catalogue (le serveur refuse sinon).
  for (const f of FILTER_DEFS) {
    if (!s.filters[f.field].length || s.dimensions.includes(f.dimension)) continue;
    for (const m of s.metrics) if (!dimensionAllowed(catalog, m, f.dimension)) out.push({ kind: 'filterIncompatible', metric: m, dimension: f.dimension });
  }
  if (s.grain) for (const m of s.metrics) if (!grainAllowed(catalog, m, s.grain)) out.push({ kind: 'grainNotAllowed', metric: m, grain: s.grain });
  if (q.from > q.to) out.push({ kind: 'periodOrder' });
  else {
    if (!isWholeMonths(q.from, q.to)) for (const m of s.metrics) if (needsWholeMonths(catalog, m)) out.push({ kind: 'wholeMonths', metric: m });
    const maxMonths = catalog?.max_months ?? 24;
    if (exceedsMonths(q.from, q.to, maxMonths)) out.push({ kind: 'periodTooLong', until: maxPeriodEnd(q.from, maxMonths), max: maxMonths });
    const maxDays = catalog?.max_day_grain_days ?? 93;
    const days = daysBetween(q.from, q.to);
    if (s.grain === 'DAY' && days > maxDays) out.push({ kind: 'dayTooLong', days, max: maxDays });
  }
  if (s.metrics.some((m) => needsProductFilter(catalog, m))) {
    if (!s.filters.product_ids.length && !s.filters.product_family_ids.length) out.push({ kind: 'quantityFilter' });
    if (!s.unit.trim()) out.push({ kind: 'quantityUnit' });
  }
  if (s.currencyMode === 'REPORTING' && catalog && !catalog.reporting_currency) out.push({ kind: 'noReportingCurrency' });
  return out;
}

// ---------------------------------------------------------------- tableau de résultat

export interface ValueColumn {
  key: string;
  metric: Metric;
  currency: string | null;
}

export interface TableRow {
  key: string;
  members: AnalyticsMember[];
  period: string | null;
  isOther: boolean;
  cells: Map<string, AnalyticsValue>;
}

export interface ResultTable {
  columns: ValueColumn[];
  rows: TableRow[];
  totals: Map<string, AnalyticsValue>;
}

export const columnKey = (v: { metric: Metric; currency: string | null }) => `${v.metric}|${v.currency ?? ''}`;

/**
 * Colonnes du résultat : une par indicateur, et en devise native une par (indicateur, devise) (contrat § 4) ; dans
 * l'ordre des indicateurs demandés, puis des devises.
 */
export function buildTable(result: AnalyticsResult): ResultTable {
  const cols = new Map<string, ValueColumn>();
  const add = (v: AnalyticsValue) => {
    const key = columnKey(v);
    if (!cols.has(key)) cols.set(key, { key, metric: v.metric, currency: v.currency });
  };
  result.totals.forEach(add);
  result.rows.forEach((r) => r.values.forEach(add));
  const order = result.query.metrics;
  const columns = [...cols.values()].sort((a, b) => order.indexOf(a.metric) - order.indexOf(b.metric) || (a.currency ?? '').localeCompare(b.currency ?? ''));
  const rows = result.rows.map((r, i) => ({
    key: `${i}:${r.is_other ? 'other' : r.members.map((m) => m.id ?? '∅').join('/')}:${r.period ?? ''}`,
    members: r.members,
    period: r.period,
    isOther: r.is_other,
    cells: new Map(r.values.map((v) => [columnKey(v), v])),
  }));
  return { columns, rows, totals: new Map(result.totals.map((v) => [columnKey(v), v])) };
}

/** Montants exclus faute de taux dans le résultat (lignes ou totaux) ? */
export function hasExcluded(result: AnalyticsResult): boolean {
  const any = (v: AnalyticsValue) => (v.excluded_without_rate ?? []).length > 0;
  return result.missing_rates.length > 0 || result.totals.some(any) || result.rows.some((r) => r.values.some(any));
}

/** Taux manquants regroupés par devise (« EUR : 2026-08, 2026-09 »), mois triés. */
export function groupMissingRates(list: MissingRate[]): { currency: string; months: string[] }[] {
  const map = new Map<string, Set<string>>();
  for (const r of list) {
    if (!map.has(r.currency)) map.set(r.currency, new Set());
    map.get(r.currency)!.add(r.month);
  }
  return [...map.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([currency, months]) => ({ currency, months: [...months].sort() }));
}

// ---------------------------------------------------------------- graphique

export interface ChartSeries {
  key: string;
  label: string;
  isOther: boolean;
  /** Valeur par tranche, en nombre pour la GÉOMÉTRIE seulement (null : non calculable). */
  points: (number | null)[];
  /** Même valeur, chaîne décimale du serveur : seule source de l'affichage (jamais un nombre flottant reformaté). */
  raw: (string | null)[];
}

export interface ChartModel {
  kind: 'line' | 'bars';
  /** Tranches (début de période) pour une courbe ; libellés des barres sinon. */
  categories: string[];
  series: ChartSeries[];
  /** Séries non dessinées (au-delà de `maxSeries`) : elles restent dans le tableau. */
  hidden: number;
}

/** Nombre décimal → géométrie seulement (jamais pour un montant affiché). */
const num = (v: string | null | undefined): number | null => {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * Modèle du graphique pour une colonne : avec un grain, une courbe par combinaison de membres (les `maxSeries` plus
 * fortes, « autres » en dernier) ; sans grain, une barre par ligne (au plus `maxBars`).
 */
export function chartModel(table: ResultTable, column: string, memberLabel: (row: TableRow) => string, opts: { grain: boolean; maxSeries?: number; maxBars?: number }): ChartModel {
  const maxSeries = opts.maxSeries ?? 6;
  const maxBars = opts.maxBars ?? 20;
  if (!opts.grain) {
    const rows = table.rows.slice(0, maxBars);
    return {
      kind: 'bars',
      categories: rows.map(memberLabel),
      series: [{ key: column, label: '', isOther: false, points: rows.map((r) => num(r.cells.get(column)?.value)), raw: rows.map((r) => r.cells.get(column)?.value ?? null) }],
      hidden: Math.max(0, table.rows.length - maxBars),
    };
  }
  const periods = [...new Set(table.rows.map((r) => r.period).filter((p): p is string => !!p))].sort();
  const groups = new Map<string, { label: string; isOther: boolean; values: Map<string, number | null>; raw: Map<string, string | null>; weight: number }>();
  for (const r of table.rows) {
    const key = r.isOther ? '__other__' : r.members.map((m) => m.id ?? '∅').join('/') || '__all__';
    if (!groups.has(key)) groups.set(key, { label: memberLabel(r), isOther: r.isOther, values: new Map(), raw: new Map(), weight: 0 });
    const g = groups.get(key)!;
    const v = num(r.cells.get(column)?.value);
    g.values.set(r.period ?? '', v);
    g.raw.set(r.period ?? '', r.cells.get(column)?.value ?? null);
    g.weight += Math.abs(v ?? 0);
  }
  const all = [...groups.entries()].sort(([, a], [, b]) => Number(a.isOther) - Number(b.isOther) || b.weight - a.weight);
  const regular = all.filter(([, g]) => !g.isOther);
  const other = all.filter(([, g]) => g.isOther);
  const kept = [...regular.slice(0, maxSeries - (other.length && regular.length >= maxSeries ? 1 : 0)), ...other].slice(0, maxSeries);
  return {
    kind: 'line',
    categories: periods,
    series: kept.map(([key, g]) => ({ key, label: g.label, isOther: g.isOther, points: periods.map((p) => (g.values.has(p) ? g.values.get(p)! : null)), raw: periods.map((p) => g.raw.get(p) ?? null) })),
    hidden: all.length - kept.length,
  };
}

/** Bornes de l'axe : de min(0, valeurs) à max(0, valeurs), jamais un intervalle nul. */
export function axisBounds(series: ChartSeries[]): { min: number; max: number } {
  const values = series.flatMap((s) => s.points).filter((v): v is number => v !== null);
  const min = Math.min(0, ...values);
  const max = Math.max(0, ...values);
  return max === min ? { min, max: min + 1 } : { min, max };
}

// ---------------------------------------------------------------- formatage

/** Abréviation d'unité dans la langue d'affichage (« h », « j » / « d », « pts »). */
function unitLabel(loc: string, key: 'hours' | 'days' | 'points'): string {
  return i18n.getFixedT(loc)(`analytics.unit.${key}`);
}

/** Valeur d'un indicateur selon son unité : montant (2 décimales + devise), volume (+ unité), %, heures, jours, ratio. */
export function formatValue(value: string | null | undefined, unit: Unit, loc: string, opts: { currency?: string | null; unitCode?: string | null } = {}): string {
  if (value === null || value === undefined) return '—';
  switch (unit) {
    case 'MONEY':
      return formatMoney(value, loc, opts.currency);
    case 'QUANTITY': {
      const q = formatQuantity(value, loc, 3);
      return opts.unitCode ? `${q} ${opts.unitCode}` : q;
    }
    case 'COUNT':
      return formatDecimal(value, loc, { min: 0, max: 1 });
    case 'PERCENT':
      return `${formatDecimal(value, loc, { min: 0, max: 1 })} %`;
    case 'HOURS':
      return `${formatDecimal(value, loc, { min: 0, max: 1 })} ${unitLabel(loc, 'hours')}`;
    case 'DAYS':
      return `${formatDecimal(value, loc, { min: 0, max: 1 })} ${unitLabel(loc, 'days')}`;
    case 'RATIO':
      return formatDecimal(value, loc, { min: 1, max: 1 });
  }
}

/** Écart signé (« +1 250,00 XAF » ; « +2,5 pts » pour un pourcentage : écart de points). */
export function formatDelta(value: string | null | undefined, unit: Unit, loc: string, opts: { currency?: string | null; unitCode?: string | null } = {}): string {
  if (value === null || value === undefined) return '—';
  const text = unit === 'PERCENT' ? `${formatDecimal(value, loc, { min: 0, max: 1 })} ${unitLabel(loc, 'points')}` : formatValue(value, unit, loc, opts);
  return withSign(value, text);
}

/** « + » devant un écart positif, sauf si l'arrondi affiché vaut zéro (« 0,00 XAF », jamais « +0,00 XAF »). */
function withSign(value: string, text: string): string {
  return new Dec(value).gt(0) && /[1-9]/.test(text.replace(/[A-Za-z].*$/, '')) ? `+${text}` : text;
}

/** Écart en % signé, 1 décimale (« +12,5 % »). */
export function formatDeltaPct(value: string | null | undefined, loc: string): string {
  if (value === null || value === undefined) return '—';
  const text = `${formatDecimal(value, loc, { min: 1, max: 1 })} %`;
  return withSign(value, text);
}

/** Sens d'un écart : hausse, baisse, stable ou inconnu. */
export function deltaDirection(value: string | null | undefined): 'up' | 'down' | 'flat' | null {
  if (value === null || value === undefined) return null;
  const d = new Dec(value);
  return d.gt(0) ? 'up' : d.lt(0) ? 'down' : 'flat';
}

/** Libellé d'une tranche selon le grain : « oct. 2026 », « sem. du 12 oct. 2026 », « 12 oct. 2026 ». */
export function periodLabel(period: string, grain: Grain | null, loc: string, weekPrefix: string): string {
  const [y, m, d] = parts(period);
  const date = new Date(Date.UTC(y, m - 1, d || 1));
  if (grain === 'MONTH') return new Intl.DateTimeFormat(loc, { month: 'short', year: 'numeric', timeZone: 'UTC' }).format(date);
  const text = new Intl.DateTimeFormat(loc, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(date);
  return grain === 'WEEK' ? `${weekPrefix} ${text}` : text;
}

/** « 2026-09 » → « septembre 2026 ». */
export function monthLabel(month: string, loc: string, style: 'long' | 'short' = 'long'): string {
  const [y, m] = month.split('-').map(Number);
  return new Intl.DateTimeFormat(loc, { month: style, year: 'numeric', timeZone: 'UTC' }).format(new Date(Date.UTC(y!, m! - 1, 1)));
}

// ---------------------------------------------------------------- comparaison des filiales

/**
 * Filiales à mettre en évidence pour un indicateur : la meilleure (rang 1) et la moins bonne (rang le plus élevé),
 * seulement s'il y a au moins deux filiales comparables (rang non nul).
 */
export function bestAndWorst(rows: EntitiesComparison['rows'], metric: Metric): { best: Set<string>; worst: Set<string> } {
  const ranked = rows.filter((r) => typeof r.ranks[metric] === 'number') as (EntitiesComparison['rows'][number] & { ranks: Record<string, number> })[];
  if (ranked.length < 2) return { best: new Set(), worst: new Set() };
  const max = Math.max(...ranked.map((r) => r.ranks[metric]!));
  const min = Math.min(...ranked.map((r) => r.ranks[metric]!));
  if (max === min) return { best: new Set(), worst: new Set() };
  return {
    best: new Set(ranked.filter((r) => r.ranks[metric] === min).map((r) => r.entity.id)),
    worst: new Set(ranked.filter((r) => r.ranks[metric] === max).map((r) => r.entity.id)),
  };
}

// ---------------------------------------------------------------- lectures

const h = (tenantId: string) => ({ 'X-Tenant-Id': tenantId });

export function useCatalog() {
  const { api, tenantId, can } = useTenant();
  return useQuery({
    queryKey: qk.analyticsCatalog(tenantId),
    enabled: can('analytics.read'),
    staleTime: 10 * 60_000,
    queryFn: () => unwrap(api.GET('/analytics/metrics', { params: { header: h(tenantId) } })),
  });
}

/** Unité d'un indicateur (catalogue du serveur, sinon la valeur du contrat). */
export function unitOf(catalog: Catalog | undefined, metric: Metric): Unit {
  return catalogMetric(catalog, metric)?.unit ?? DEFAULT_UNITS[metric];
}

export function useAnalyticsResult(body: AnalyticsQuery | null) {
  const { api, tenantId } = useTenant();
  return useQuery({
    queryKey: qk.analyticsQuery(tenantId, body ?? {}),
    enabled: !!body,
    placeholderData: keepPreviousData,
    queryFn: () => unwrap(api.POST('/analytics/query', { params: { header: h(tenantId) }, body: body! })),
  });
}

export async function downloadAnalyticsCsv(api: Api, tenantId: string, body: AnalyticsQuery): Promise<void> {
  const blob = await unwrap(api.POST('/analytics/export', { params: { header: h(tenantId) }, body, parseAs: 'blob' }));
  saveBlob(blob as unknown as Blob, `analyse-${body.from}-${body.to}.csv`);
}

/** Rafraîchissement de l'état des actualisations : 5 s pendant une actualisation, 60 s sinon. */
export function refreshInterval(status: RefreshStatus | undefined): number {
  return status?.running ? 5_000 : 60_000;
}

export function useRefreshStatus() {
  const { api, tenantId, can } = useTenant();
  return useQuery({
    queryKey: qk.analyticsRefresh(tenantId),
    enabled: can('analytics.read'),
    queryFn: () => unwrap(api.GET('/analytics/refresh', { params: { header: h(tenantId) } })),
    refetchInterval: (q) => refreshInterval(q.state.data),
    refetchIntervalInBackground: false,
  });
}
